/**
 * ContactPanel — the inbox's right-hand "basic CRM" column.
 *
 * A contact in Baatly IS the conversation document, so this panel surfaces the
 * contact-level record the inbox already owns: identity, channel, opt-out,
 * assignee, **tags** (editable, suggestions from the tenant's existing tag
 * set so the same tag can be applied consistently across contacts), and a
 * team-authored **notes** timeline (author + time). Notes/tag edits are
 * audited server-side, which is what powers "who updated".
 *
 * Deliberately contact-level only — funnel stage / score live on the Lead
 * (the opportunity record) and arrive once conversations link to leads.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Divider,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  Avatar,
  Badge,
  alpha,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  PersonOutline,
  LocalOffer,
  StickyNote2Outlined,
  FileDownloadOutlined,
  ArchiveOutlined,
  UnarchiveOutlined,
  InfoOutlined,
  HistoryOutlined,
  FlagOutlined,
} from '@mui/icons-material';
import { TbLayoutSidebarRightCollapse, TbLayoutSidebarRightExpand } from 'react-icons/tb';
import ContactNotesEditor from '@/components/contacts/ContactNotesEditor';
import ContactTagsEditor from '@/components/contacts/ContactTagsEditor';
import InstagramLogo from '@/components/common/InstagramLogo';

export interface ContactPanelContact {
  _id: string;
  customerPhone: string;
  customerName?: string;
  customerEmail?: string;
  channel?: string;
  status?: string;
  tags?: string[];
  assignedTo?: { _id?: string; firstName?: string; lastName?: string; name?: string };
  optOut?: { isOptedOut?: boolean };
  customerCountryCode?: string;
  /** Instagram only — @handle from Meta's User Profile API. */
  username?: string;
  /** Instagram only — ephemeral per Meta docs (URLs expire within days), display-only. */
  profilePictureUrl?: string;
}

/** The same actions as the chat header's ⋮ menu, surfaced on the panel so an
 *  agent can act without leaving the contact record. The HOST decides which
 *  are available (permissions, channel, routing state) — the panel only renders
 *  what it's given, so the two surfaces can never disagree. */
export interface ContactPanelActions {
  /** Stop the bot and take over this chat (routing state is 'bot' or 'pending_human'). WhatsApp only — Instagram has no bot yet. */
  onStopBot?: () => void;
  /** Open the assign-to-team-member dialog (requires full access to the channel's module). Ownership only — no bot effect. */
  onAssign?: () => void;
  /** Resume automated replies; assignment is kept (routing state is 'human' or 'pending_human'). WhatsApp only. */
  onResumeBot?: () => void;
  /** Self-assign an unclaimed (or already-mine) conversation — Instagram's
   *  equivalent of WhatsApp's implicit claim-via-reply; Instagram has no bot
   *  to hand back to, so this is the primary "take this chat" action there. */
  onClaim?: () => void;
  /** Clear the assignment — Instagram's equivalent of hand-back-to-bot minus
   *  the bot part (there is none to hand back to). */
  onUnassign?: () => void;
  /** Download the CSV transcript. */
  onExport?: () => void;
  /** Archive / restore — label follows `isArchived`. */
  onArchive?: () => void;
  isArchived?: boolean;
}

/** Conversation-technical fields — merged in from what used to be the chat
 *  header's separate "Details" tab. Pre-formatted by the host (ChatLogs.tsx
 *  already computes all of this for the same purpose), so this component
 *  stays a plain renderer with no formatting/date-math logic of its own. */
export interface ContactPanelConversationDetails {
  messageCount: number;
  status: string;
  lastActivity: string;
  lastCustomerReply: string;
  /** e.g. "Open · 3h 12m" / "Expired" / "Not opened". */
  windowLabel: string;
  windowColor?: 'success' | 'warning';
}

/** One routing.history row, pre-formatted the same way (see
 *  ChatLogs.tsx's describeRoutingEvent + formatTimeAgo — kept there so this
 *  component doesn't need to import ChatLogs' internal helpers/types). */
export interface ContactPanelRoutingEvent {
  id: string;
  color: 'info' | 'warning' | 'success' | 'default';
  label: string;
  detail?: string;
  timeAgo: string;
}

interface ContactPanelProps {
  contact: ContactPanelContact;
  /** Fired after a successful tag save so the host can patch its list/selection. */
  onTagsSaved: (contactId: string, tags: string[]) => void;
  /** Whether the current user may edit (tags/notes) — read-only otherwise. */
  canEdit: boolean;
  /** Whether this contact is flagged for review — renders the review card up
   *  top (most actionable fact about a contact, so it goes first). Moved here
   *  from a banner over the live chat thread. */
  flagged?: boolean;
  /** Pre-formatted reason text (see ChatLogs.tsx's flagLabel) — required
   *  alongside `flagged`, ignored otherwise. */
  flagReason?: string;
  /** Clears the flag — omit to render the card without a "Mark reviewed" button. */
  onMarkReviewed?: () => void;
  /** Disables the "Mark reviewed" button while the clear request is in flight. */
  markReviewedPending?: boolean;
  /** Conversation-technical fields (message count, 24h window, etc.) — omit to
   *  hide the "Conversation" section entirely. */
  conversationDetails?: ContactPanelConversationDetails;
  /** Newest-first; omit or pass an empty array to hide the "Routing history"
   *  section (same condition the old Details tab used). */
  routingHistory?: ContactPanelRoutingEvent[];
  /** Whether the current user may delete a note — DELETE method-derives to
   *  'full' server-side, a strictly higher tier than the 'edit' canEdit checks.
   *  Defaults to canEdit for backward compatibility; a host should pass the
   *  real 'full'-level check. */
  canDeleteNotes?: boolean;
  /** Whether the current user can see tags/notes at all. This panel is hosted
   *  from Chat Logs, gated on `activity.chatLogs` (WhatsApp) or `instagram` —
   *  but tags/notes live under the SEPARATE `contacts` module gate for
   *  WhatsApp (Instagram's own tags/notes are gated by the `instagram` module
   *  itself, same as the rest of that channel's inbox). A role with the
   *  primary module and not the tags/notes one would otherwise hit a doomed
   *  fetch and a confusing inline error (or an interrupting permission
   *  dialog) for a module it was never granted. Defaults to true for
   *  backward compatibility; the host must pass the check matching
   *  `contact.channel` (`hasPermission('contacts', 'read')` for WhatsApp,
   *  `hasPermission('instagram', 'read')` for Instagram). */
  canViewContactExtras?: boolean;
  actions?: ContactPanelActions;
  /** Controlled by the host (ChatLogs.tsx owns the persisted preference) so
   *  this component stays a pure renderer. Defaults to true for backward
   *  compatibility. */
  open?: boolean;
  /** Fired when the panel's own collapse/expand control is clicked — omit to
   *  render without one (e.g. a host that drives `open` some other way). */
  onToggle?: () => void;
}

function Section({
  icon,
  title,
  count,
  defaultOpen = true,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
          {title}
          {typeof count === 'number' && (
            <Typography component="span" variant="caption" color="text.secondary" ml={0.75}>
              ({count})
            </Typography>
          )}
        </Typography>
        {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 2, pb: 2 }}>{children}</Box>
      </Collapse>
      <Divider />
    </Box>
  );
}

const ContactPanel: React.FC<ContactPanelProps> = ({
  contact,
  onTagsSaved,
  canEdit,
  flagged,
  flagReason,
  onMarkReviewed,
  markReviewedPending,
  conversationDetails,
  routingHistory,
  canDeleteNotes,
  canViewContactExtras = true,
  actions,
  open = true,
  onToggle,
}) => {
  // Tags and notes both live in shared editors (components/contacts/) so this
  // panel and the contact detail page can never drift apart on how they save.
  // Only the collapsible-header counts stay here, reported up by each editor.
  const [tagCount, setTagCount] = useState(contact.tags?.length || 0);
  const [noteCount, setNoteCount] = useState(0);

  const assigneeName = contact.assignedTo
    ? `${contact.assignedTo.firstName || ''} ${contact.assignedTo.lastName || ''}`.trim() ||
      contact.assignedTo.name
    : undefined;

  // Collapsed: a slim icon-rail (Gmail/Notion-style), not a zero-width clip of
  // the full panel — a genuinely different, minimal view rather than the same
  // content squeezed to nothing. The one control lives ON the rail itself, at
  // the panel's own right-hand edge — not a floating button in the gap
  // between columns, which read as a stray, easy-to-miss element.
  if (!open) {
    return (
      <Box
        sx={{
          width: 44,
          flexShrink: 0,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          alignItems: 'center',
          pt: 1.5,
          // Slightly darker than the list/chat columns (background.paper) —
          // a secondary/detail surface, not a third copy of the same white.
          bgcolor: 'background.default',
          // No left border: sits flush against the chat column (no gap
          // between the three panels) — its right border is the seam.
          border: '1px solid #e2e8f0',
          borderLeft: 0,
          boxShadow: '0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.06)',
          // ChatLogs.tsx's own root Box cancels AdminLayout's page padding with
          // a negative margin, so the full viewport height is genuinely
          // available — no offset to subtract (this panel only ever renders at
          // md+ anyway, same breakpoint that negative margin applies at).
          height: '100vh',
        }}
      >
        <Tooltip title="Show contact details" placement="left">
          <IconButton
            onClick={onToggle}
            sx={{
              color: '#0f172a',
              bgcolor: alpha('#0f172a', 0.06),
              borderRadius: 1.5,
              '&:hover': { bgcolor: alpha('#0f172a', 0.12) },
            }}
          >
            <TbLayoutSidebarRightExpand size={22} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: { md: 300, lg: 340 },
        flexShrink: 0,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        // Slightly darker than the list/chat columns (background.paper) — a
        // secondary/detail surface, not a third copy of the same white.
        bgcolor: 'background.default',
        // No left border: sits flush against the chat column (no gap
        // between the three panels) — its right border is the seam.
        border: '1px solid #e2e8f0',
        borderLeft: 0,
        boxShadow: '0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.06)',
        // Hugs its own content instead of forcing the full viewport height —
        // Assigned to/Tags/Notes are usually much shorter than the chat
        // thread, and a fixed 100vh here just left dead space below the last
        // section. Capped at 100vh (ChatLogs.tsx's root Box cancels
        // AdminLayout's page padding, so that's the real available height)
        // so long content still scrolls internally instead of pushing the
        // page taller than the viewport.
        height: 'auto',
        maxHeight: '100vh',
        overflowY: 'auto',
      }}
    >
      {/* Collapse control — lives on the panel itself, not a floating button
          in the gap between columns. */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 1 }}>
        <Tooltip title="Hide contact details" placement="left">
          <IconButton
            onClick={onToggle}
            sx={{
              color: '#0f172a',
              bgcolor: alpha('#0f172a', 0.06),
              borderRadius: 1.5,
              '&:hover': { bgcolor: alpha('#0f172a', 0.12) },
            }}
          >
            <TbLayoutSidebarRightCollapse size={22} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Identity */}
      <Box sx={{ p: 2, textAlign: 'center' }}>
        {/* MUI Avatar, not a CSS background: when the photo url fails (an
            expired Meta CDN link, before our stored copy exists) Avatar falls
            back to the initial. A background-image just silently doesn't
            paint AND the initial was suppressed because a url existed — which
            rendered an empty coloured circle. */}
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          invisible={contact.channel !== 'instagram'}
          badgeContent={
            <Box sx={{ bgcolor: 'background.paper', borderRadius: '6px', p: '2px', display: 'flex', lineHeight: 0 }}>
              <InstagramLogo size={16} />
            </Box>
          }
          sx={{ display: 'block', width: 'fit-content', mx: 'auto', mb: 1 }}
        >
          <Avatar
            {...(contact.profilePictureUrl ? { src: contact.profilePictureUrl } : {})}
            alt={contact.customerName || contact.username || 'Contact'}
            sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: 22, fontWeight: 700 }}
          >
            {(contact.customerName || contact.username || contact.customerPhone || '?').trim().charAt(0).toUpperCase()}
          </Avatar>
        </Badge>
        <Typography variant="subtitle1" fontWeight={700} noWrap>
          {contact.customerName || (contact.username ? `@${contact.username}` : 'Unknown contact')}
        </Typography>
        {/* customerPhone is always '' for an Instagram thread (no phone number
         *  exists there) — rendering it unconditionally left a blank line on
         *  every Instagram contact. Show the @handle instead when there's a
         *  resolved customerName to also display (username alone already
         *  covers the no-customerName case above). */}
        {contact.customerPhone ? (
          <Typography variant="body2" color="text.secondary" noWrap>
            {contact.customerPhone}
          </Typography>
        ) : (
          contact.username &&
          contact.customerName && (
            <Typography variant="body2" color="text.secondary" noWrap>
              @{contact.username}
            </Typography>
          )
        )}
        {contact.customerEmail && (
          <Typography variant="body2" color="text.secondary" noWrap>
            {contact.customerEmail}
          </Typography>
        )}
        <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap mt={1}>
          {contact.channel && <Chip size="small" label={contact.channel} />}
          {contact.status && <Chip size="small" variant="outlined" label={contact.status} />}
          {contact.customerCountryCode && <Chip size="small" variant="outlined" label={contact.customerCountryCode} />}
          {contact.optOut?.isOptedOut && <Chip size="small" color="warning" label="Opted out" />}
        </Stack>
        {(actions?.onExport || actions?.onArchive) && (
          <Stack direction="row" spacing={1} justifyContent="center" mt={1.5}>
            {actions.onExport && (
              <Button size="small" variant="outlined" startIcon={<FileDownloadOutlined fontSize="small" />} onClick={actions.onExport}>
                Export
              </Button>
            )}
            {actions.onArchive && (
              <Button
                size="small"
                variant="outlined"
                color={actions.isArchived ? 'primary' : 'inherit'}
                startIcon={actions.isArchived ? <UnarchiveOutlined fontSize="small" /> : <ArchiveOutlined fontSize="small" />}
                onClick={actions.onArchive}
              >
                {actions.isArchived ? 'Unarchive' : 'Archive'}
              </Button>
            )}
          </Stack>
        )}
      </Box>
      <Divider />

      {/* Flag review — the most actionable fact about a contact, so it goes
          first, above Assigned to/Tags/Notes. Moved here from a banner that
          used to sit on top of the live chat thread. Non-collapsible (unlike
          the Section cards below): a flag needing review shouldn't be
          something a click can hide out of view. */}
      {flagged && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              px: 2,
              py: 1.25,
              borderLeft: '3px solid #e11d48',
              bgcolor: '#fff1f2',
            }}
          >
            <FlagOutlined sx={{ fontSize: 16, color: '#e11d48', flexShrink: 0, mt: 0.25 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: '#881337', fontWeight: 600, display: 'block' }}>
                {flagReason || 'Flagged for review'}
              </Typography>
              {onMarkReviewed && (
                <Button
                  size="small"
                  disabled={!!markReviewedPending}
                  onClick={onMarkReviewed}
                  sx={{ mt: 0.5, minWidth: 0, px: 0, color: '#e11d48', fontWeight: 700, fontSize: '0.75rem', textTransform: 'none' }}
                >
                  Mark reviewed
                </Button>
              )}
            </Box>
          </Box>
          <Divider />
        </>
      )}

      {/* Assignee (read-only here — bot-control/assign live in the buttons
          below; the chat header's ⋮ menu is the mobile fallback, since this
          panel doesn't render below the md breakpoint) */}
      <Section icon={<PersonOutline fontSize="small" />} title="Assigned to">
        <Typography variant="body2" color={assigneeName ? 'text.primary' : 'text.secondary'}>
          {assigneeName || 'Unassigned'}
        </Typography>
        {(actions?.onStopBot || actions?.onAssign || actions?.onResumeBot || actions?.onClaim || actions?.onUnassign) && (
          <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
            {actions.onStopBot && (
              <Tooltip title="Pause automated replies and take over this chat">
                <Button size="small" variant="contained" onClick={actions.onStopBot}>
                  Stop bot
                </Button>
              </Tooltip>
            )}
            {actions.onClaim && (
              <Tooltip title="Take this unassigned chat yourself">
                <Button size="small" variant="contained" onClick={actions.onClaim}>
                  Assign to me
                </Button>
              </Tooltip>
            )}
            {actions.onAssign && (
              <Button size="small" variant="outlined" onClick={actions.onAssign}>
                {assigneeName ? 'Reassign…' : 'Assign…'}
              </Button>
            )}
            {actions.onUnassign && (
              <Tooltip title="Clear the assignment">
                <Button size="small" variant="outlined" color="inherit" onClick={actions.onUnassign}>
                  Unassign
                </Button>
              </Tooltip>
            )}
            {actions.onResumeBot && (
              <Tooltip title="Resume automated replies. Assignment is kept.">
                <Button size="small" variant="outlined" color="inherit" onClick={actions.onResumeBot}>
                  Hand back to bot
                </Button>
              </Tooltip>
            )}
          </Stack>
        )}
      </Section>

      {/* Tags */}
      <Section icon={<LocalOffer fontSize="small" />} title="Tags" {...(canViewContactExtras ? { count: tagCount } : {})}>
        {canViewContactExtras ? (
          <ContactTagsEditor
            contactId={contact._id}
            tags={contact.tags || []}
            canEdit={canEdit}
            onSaved={onTagsSaved}
            onDraftCountChange={setTagCount}
            channel={contact.channel === 'instagram' ? 'instagram' : 'whatsapp'}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            You don't have access to this contact's tags.
          </Typography>
        )}
      </Section>

      {/* Notes */}
      <Section icon={<StickyNote2Outlined fontSize="small" />} title="Notes" {...(canViewContactExtras ? { count: noteCount } : {})}>
        {canViewContactExtras ? (
          <ContactNotesEditor
            contactId={contact._id}
            canEdit={canEdit}
            {...(canDeleteNotes !== undefined ? { canDelete: canDeleteNotes } : {})}
            onCountChange={setNoteCount}
            channel={contact.channel === 'instagram' ? 'instagram' : 'whatsapp'}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            You don't have access to this contact's notes.
          </Typography>
        )}
      </Section>

      {/* Conversation — the message-thread-technical fields that used to live on
          the chat header's separate "Details" tab. Collapsed by default: this is
          occasional/diagnostic lookup, not something an agent checks every time
          they open a chat, unlike Tags/Notes above. */}
      {conversationDetails && (
        <Section icon={<InfoOutlined fontSize="small" />} title="Conversation" defaultOpen={false}>
          <Stack spacing={0}>
            {[
              { label: 'Messages', value: String(conversationDetails.messageCount) },
              { label: 'Status', value: conversationDetails.status },
              { label: 'Last activity', value: conversationDetails.lastActivity },
              { label: 'Last customer reply', value: conversationDetails.lastCustomerReply },
              {
                label: '24h window',
                value: conversationDetails.windowLabel,
                color:
                  conversationDetails.windowColor === 'success'
                    ? '#16a34a'
                    : conversationDetails.windowColor === 'warning'
                      ? '#b45309'
                      : undefined,
              },
            ].map(({ label, value, color }) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1,
                  py: 0.75,
                  borderBottom: '1px solid #f1f5f9',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="caption" fontWeight={600} sx={{ color: color || 'text.primary' }} noWrap>
                  {value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Section>
      )}

      {/* Routing history — same data the old Details tab showed as a wide Table;
          a stacked timeline fits this panel's width better than a 3-column table. */}
      {routingHistory && routingHistory.length > 0 && (
        <Section icon={<HistoryOutlined fontSize="small" />} title="Routing history" defaultOpen={false}>
          <Stack spacing={1.25}>
            {routingHistory.map((event) => (
              <Box
                key={event.id}
                sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid #eef2f7' }}
              >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: event.color === 'default' ? 'text.secondary' : `${event.color}.main` }}
                  >
                    {event.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {event.timeAgo}
                  </Typography>
                </Stack>
                {event.detail && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                    {event.detail}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Section>
      )}
    </Box>
  );
};

export default ContactPanel;
